import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, Link, useLocation, Outlet } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import { getFirestore, collection, getDocs, onSnapshot } from "firebase/firestore";
import app from "../../firebase";
import AssessmentFee from "./AssessmentFee";
import EnrollmentFee from "./EnrollmentFee";

const SIDEBAR = [
  { key: "home", label: "Home" },
  { key: "assessment-fee", label: "Assessment Fee" },
  { key: "enrollment-fee", label: "Enrollment Fee" },
];

export default function FinancesDashboard() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("home");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useSession();

  // Redirect to /services when user logs out from another tab
  useEffect(() => {
    if (currentUser === null) {
      navigate("/services");
    }
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/services");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  useEffect(() => {
    const db = getFirestore(app);
    const studentsCol = collection(db, "students");
    const unsubscribe = onSnapshot(studentsCol, (snap) => {
      setStudents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (err) => {
      console.error("Error fetching students:", err);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Sidebar click handler
  const handleNav = (key) => {
    setActive(key);
    if (key === "home") navigate("/finances/dashboard");
    if (key === "assessment-fee") navigate("/finances/dashboard/assessment-fee");
    if (key === "enrollment-fee") navigate("/finances/dashboard/enrollment-fee");
  };

  // Simulate logged-in user (replace with real user context if available)
  const financeUser = { name: "finance", initials: "TF" };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Sidebar */}
      <div style={{ width: 240, background: '#155c2b', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'stretch', padding: '32px 0', minHeight: '100vh' }}>
        <div style={{ fontWeight: 700, fontSize: 24, textAlign: 'center', marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        <nav>
          <Link
            to="/finances/dashboard"
            style={{
              display: "block",
              padding: "14px 32px",
              background: location.pathname === "/finances/dashboard" ? "#e67e22" : "transparent",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 16,
              marginBottom: 8,
              borderRadius: 6,
              transition: "background 0.2s, color 0.2s",
              boxShadow: location.pathname === "/finances/dashboard" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Home
          </Link>
          <Link
            to="/finances/dashboard/assessment-fee"
            style={{
              display: "block",
              padding: "14px 32px",
              background: location.pathname === "/finances/dashboard/assessment-fee" ? "#e67e22" : "transparent",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 16,
              marginBottom: 8,
              borderRadius: 6,
              transition: "background 0.2s, color 0.2s",
              boxShadow: location.pathname === "/finances/dashboard/assessment-fee" ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Assessment Fee
          </Link>
          <Link
            to="/finances/dashboard/enrollment-fee"
            style={{
              display: "block",
              padding: "14px 32px",
              background: location.pathname.startsWith("/finances/dashboard/enrollment-fee") ? "#e67e22" : "transparent",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 16,
              marginBottom: 8,
              borderRadius: 6,
              transition: "background 0.2s, color 0.2s",
              boxShadow: location.pathname.startsWith("/finances/dashboard/enrollment-fee") ? "0 2px 8px #e67e22aa" : "none"
            }}
          >
            Enrollment Fee
          </Link>
        </nav>
      </div>
      {/* Main Content Area */}
      <div style={{ flex: 1, background: '#f5f5f5', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Top Bar */}
        <div style={{ height: 80, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '0 40px', position: 'relative', zIndex: 10 }}>
          <span style={{ color: '#155c2b', fontWeight: 600, fontSize: 18, marginRight: 18 }}>{financeUser.name}</span>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#fff', border: '2px solid #d97d2c', color: '#17643a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18 }}>
                {financeUser.initials}
              </div>
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: 8,
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                zIndex: 20,
                minWidth: 200,
                overflow: "hidden"
              }}>
                <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>
                  Finance Officer
                </div>
                <button
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    width: "100%",
                    display: "block",
                    padding: "12px 16px",
                    color: "#333",
                    textDecoration: "none",
                    borderBottom: "1px solid #f0f0f0",
                    fontSize: 14,
                    cursor: "pointer",
                    transition: "background 0.2s",
                    background: "transparent",
                    border: "none",
                    textAlign: "left"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#f9f9f9"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Profile
                </button>
                <button
                  onClick={() => setDropdownOpen(false)}
                  style={{
                    width: "100%",
                    display: "block",
                    padding: "12px 16px",
                    color: "#333",
                    textDecoration: "none",
                    borderBottom: "1px solid #f0f0f0",
                    fontSize: 14,
                    cursor: "pointer",
                    transition: "background 0.2s",
                    background: "transparent",
                    border: "none",
                    textAlign: "left"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#f9f9f9"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Settings
                </button>
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    handleLogout();
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "transparent",
                    border: "none",
                    color: "#e67e22",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={(e) => e.target.style.background = "#fef5f0"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Routed Content */}
        <div style={{ flex: 1 }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function HomePage({ students, loading }) {
  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ color: '#155c2b', marginBottom: 24 }}>Finances Dashboard Home</h2>
      <p>Welcome to the Finances Dashboard. Use the sidebar to navigate to Assessment Fee or Enrollment Fee pages.</p>
      {loading ? (
        <div>Loading students...</div>
      ) : (
        <table style={{ width: "100%", background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderCollapse: "collapse", marginTop: 32 }}>
          <thead>
            <tr style={{ background: "#e8e8e8" }}>
              <th style={{ padding: 12, textAlign: "left" }}></th>
              <th style={{ padding: 12, textAlign: "left" }}></th>
              <th style={{ padding: 12, textAlign: "left" }}></th>
              <th style={{ padding: 12, textAlign: "left" }}></th>
            </tr>
          </thead>
          <tbody>
            {students.map(student => (
              <tr key={student.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 10 }}>{student.idNumbers}</td>
                <td style={{ padding: 10 }}>{student.firstNames} {student.lastNames}</td>
                <td style={{ padding: 10 }}>{student.programs}</td>
                <td style={{ padding: 10 }}>{student.financeStatuss }</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
